import { useCallback, useMemo } from "react";
import { useSearchParams } from "react-router-dom";

/** 1 ページの件数。 */
export const PARTNERS_PAGE_SIZE = 50;

/** 取引状態の絞り込み。`all` は取引中・取引停止の両方。 */
export type PartnerTradingFilter = "all" | "active" | "stopped";

/** 用途の絞り込み。`all` は得意先・直送先の両方（兼ねる相手は両方に出る）。 */
export type PartnerRoleFilter = "all" | "client" | "ship_to";

export interface PartnerListParamsState {
  page: number;
  keyword: string;
  trading: PartnerTradingFilter;
  role: PartnerRoleFilter;
  sortKey: string;
  ascending: boolean;
}

const DEFAULTS: PartnerListParamsState = {
  page: 1,
  keyword: "",
  // マスタ台帳なので既定は全件（取引停止も見える）。得意先の選択肢を出す場面は
  // 別途 disabled=false で引く。
  trading: "all",
  // 用途も既定は全件。得意先・直送先の選択肢を出す場面は、それぞれ
  // is_client=true / is_ship_to=true で別途引く。
  role: "all",
  sortKey: "partner_code",
  ascending: true,
};

/**
 * 取引先一覧の検索条件・並び替え・ページを URL のクエリに持つ。
 *
 * 見積一覧（[[useQuotationListParams]]）と同じ方針。URL に置くのはリロードで
 * 復元されるだけでなく、URL を共有でき、ブラウザの戻る/進むが効くため。
 */
export function usePartnerListParams() {
  const [searchParams, setSearchParams] = useSearchParams();

  const params = useMemo<PartnerListParamsState>(() => {
    const page = Number(searchParams.get("page") ?? DEFAULTS.page);
    const trading = searchParams.get("trading");
    const role = searchParams.get("role");
    return {
      page: Number.isFinite(page) && page >= 1 ? page : DEFAULTS.page,
      keyword: searchParams.get("keyword") ?? DEFAULTS.keyword,
      trading:
        trading === "active" || trading === "stopped" ? trading : DEFAULTS.trading,
      role: role === "client" || role === "ship_to" ? role : DEFAULTS.role,
      sortKey: searchParams.get("sort_key") ?? DEFAULTS.sortKey,
      ascending: (searchParams.get("ascending") ?? "true") === "true",
    };
  }, [searchParams]);

  const update = useCallback(
    (patch: Partial<PartnerListParamsState>) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          const merged = { ...params, ...patch };
          // 条件を変えたらページを 1 に戻す（page 自体の変更を除く）。
          if (patch.page === undefined) merged.page = 1;

          const set = (key: string, value: string, fallback: string) =>
            value === fallback ? next.delete(key) : next.set(key, value);

          set("page", String(merged.page), String(DEFAULTS.page));
          set("keyword", merged.keyword, DEFAULTS.keyword);
          set("trading", merged.trading, DEFAULTS.trading);
          set("role", merged.role, DEFAULTS.role);
          set("sort_key", merged.sortKey, DEFAULTS.sortKey);
          set("ascending", String(merged.ascending), String(DEFAULTS.ascending));
          return next;
        },
        { replace: true },
      );
    },
    [params, setSearchParams],
  );

  const reset = useCallback(() => {
    setSearchParams(new URLSearchParams(), { replace: true });
  }, [setSearchParams]);

  /** API に渡す disabled。全件のときは未指定にする。 */
  const disabledParam =
    params.trading === "all" ? undefined : params.trading === "stopped";

  // 用途は 2 つの独立したフラグなので、「得意先」は is_client=true だけを渡す
  // （is_ship_to=false は渡さない）。兼ねる相手を落とさないため。
  const isClientParam = params.role === "client" ? true : undefined;
  const isShipToParam = params.role === "ship_to" ? true : undefined;

  return { params, update, reset, disabledParam, isClientParam, isShipToParam };
}
